import { useContext, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation } from '@tanstack/react-query';
import '../../controlledgoodsmodal.css';
import { Input, Password } from '../../Input/Input';
import isoDateConverter from '../../isoDateConverter';
import { StoreContext } from '../../Store';
import { confirmUsingAuthentication } from '../../../api/v2/user';

export const ArrivalConfirmation = ({ item, show, onHide, onConfirm, defaultDate, hasSfd, sfdArrived }) => {
  const store = useContext(StoreContext);
  const [errorText, setErrorText] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const authConfirmMutation = useMutation({
    mutationFn: async (payload) => confirmUsingAuthentication(payload),
  });

  const passwordRequired = hasSfd && !sfdArrived;

  useEffect(() => {
    if (!store.portArrivalDate[0]) {
      store.portArrivalDate[1](defaultDate);
    }
  }, [defaultDate]);

  const validateDateTime = () => {
    const nowDT = new Date();
    const submittedDT = new Date(item.createdDate);
    const selectedDT = new Date(`${store.portArrivalDate[0]} ${store.portArrivalTime[0]} UTC`);

    if (isNaN(selectedDT)) {
      setErrorText('Please select a valid date and time.');
      return false;
    }

    if (selectedDT < submittedDT) {
      setErrorText('Port arrival time must be after submission time.');
      return false;
    }

    if (selectedDT > nowDT) {
      setErrorText('Port arrival time cannot be in the future.');
      return false;
    }

    return true;
  };

  const validateLogin = async () => {
    setPasswordError(false);
    try {
      const response = await authConfirmMutation.mutateAsync({
        username: sessionStorage.getItem('name'),
        password: password,
      });

      if (response?.success === true) {
        return true;
      } else {
        setPasswordError(true);
      }
    } catch {
      setPasswordError(true);
    }
  };

  return (
    <Modal centered size="md" animation={false} show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>{sfdArrived ? 'SFD Acceptance Date & Time' : 'Port Arrival'}</Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
      </Modal.Header>

      <section>
        <Modal.Body>
          <span>
            {hasSfd ? (
              <span>Please enter the SFD Acceptance Date and Time in Coordinated Universal Time (UTC)</span>
            ) : (
              <>
                <span>
                  Please select the port arrival date/time as this is required to produce a Supplementary Declaration.
                </span>
                <br />
                <span>Please enter the below date in Coordinated Universal Time (UTC).</span>
              </>
            )}
          </span>
          <div className="sub-heading arrival-error">{errorText}</div>

          <div className="sub-heading">Load Submitted: {isoDateConverter(item?.createdDate)}</div>

          <div className="d-flex flex-row py-3 px-3 gap-3">
            <Input
              label={sfdArrived ? 'DMSACC Arrival Date' : 'Port Arrival Date'}
              name="date"
              type="date"
              required
              value={store.portArrivalDate[0]}
              onChange={(e) => store.portArrivalDate[1](e.target.value)}
            />

            <Input
              label={sfdArrived ? 'DMSACC Arrival Time' : 'Port Arrival time'}
              name="time"
              type="time"
              required
              value={store.portArrivalTime[0]}
              onChange={(e) => store.portArrivalTime[1](e.target.value)}
            />
          </div>
          {passwordRequired && (
            <div>
              <div className="px-2">You must enter your username and password to confirm the DMSACC Date and time</div>
              <div style={{ paddingTop: '15px', paddingBottom: '15px' }}>
                <Input
                  name="userName"
                  type="text"
                  label="Username"
                  value={sessionStorage.getItem('name')}
                  hideLabel={true}
                  disabled
                />
              </div>
              <Password
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={'Confirm Password'}
                hideLabel={true}
                autocomplete="new-password"
              />
              {passwordError && (
                <span className="text-danger">Authentication failed. Please check your credentials.</span>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer
          class="py-3 px-3"
          style={{
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
          }}
        >
          <div className="push-left-right">
            <button
              className="blue-button-newload red"
              onClick={(event) => {
                onHide(event);
                setErrorText('');
              }}
              style={{ display: 'flex', borderRadius: '10px' }}
            >
              {' '}
              Back
            </button>
            <button
              className="blue-button-newload green"
              onClick={async (event) => {
                setSubmitting(true);
                setErrorText('');

                if (validateDateTime()) {
                  try {
                    const authSuccess = passwordRequired ? await validateLogin() : true;
                    if (authSuccess) {
                      onConfirm();
                      onHide(event);
                    }
                  } catch {
                    setSubmitting(false);
                  }
                }
                setSubmitting(false);
              }}
              style={{ display: 'flex', borderRadius: '10px' }}
              disabled={submitting || (passwordRequired && !password.length)}
            >
              Submit
            </button>
          </div>
        </Modal.Footer>
      </section>
    </Modal>
  );
};

export default ArrivalConfirmation;
